import { useContext } from 'react';

import {
	useGetOnboardingState,
	deserializeState,
} from '@confluence/onboarding-helpers/entry-points/hooks/useOnboardingState';
import { ONBOARDING_EXPERIENCES } from '@confluence/onboarding-helpers/entry-points/constants/onboarding-state-constants';
import { useIsXflowUser } from '@confluence/onboarding-helpers/entry-points/hooks/useIsXflowUser';
import { UNSAFE_noExposureExp } from '@confluence/feature-experiments';
import { SPAViewContext } from '@confluence/spa-view-context';
interface UseSimplifyInitialNavProps {
	isPersonalSpace: boolean;
	spaceCreatedAtDate?: Date;
}

export const useSimplifyInitialNav = ({
	isPersonalSpace,
	spaceCreatedAtDate,
}: UseSimplifyInitialNavProps) => {
	const isInExperimentControl = getExperimentCohort() !== 'treatment';

	const { isSiteAdmin } = useContext(SPAViewContext);

	const skipQueries =
		!isPersonalSpace ||
		!isSiteAdmin ||
		isInExperimentControl ||
		isSpaceOlderThan24Hours(spaceCreatedAtDate);

	const { isXflowUser, isLoading: isXflowUserLoading } = useIsXflowUser({ skip: skipQueries });

	const { data: onboardingStateData, loading: isFoundingAdminLoading } = useGetOnboardingState(
		[ONBOARDING_EXPERIENCES.IS_FOUNDING_ADMIN],
		skipQueries,
	);

	if (skipQueries) {
		return { showSpaceItems: true };
	}

	const { isFoundingAdmin } = deserializeState(onboardingStateData);

	return {
		showSpaceItems:
			(!isXflowUserLoading && isXflowUser) || (!isFoundingAdminLoading && !isFoundingAdmin),
	};
};

type Cohort = 'control' | 'treatment';

const getExperimentCohort = (): Cohort => {
	// eslint-disable-next-line confluence-feature-gating/unsafe-no-exposure
	const [expConfig] = UNSAFE_noExposureExp('cfind-2776-simplify-initial-conf-nav'); // Experiment exposure is fired in the backend
	const experimentVal = expConfig.get('cohort', 'control');
	return experimentVal as Cohort;
};

const isSpaceOlderThan24Hours = (createdAtDate?: Date): boolean => {
	if (!createdAtDate) {
		return false;
	}
	const createdAtTimestamp = createdAtDate.getTime();
	const millisecondsSinceCreation = Date.now() - createdAtTimestamp;
	const millisecondsIn24Hours = 24 * 60 * 60 * 1000;
	return millisecondsSinceCreation > millisecondsIn24Hours;
};
